/**
 * useInfiniteScroll — IntersectionObserver 기반 sentinel ref.
 *
 * sentinel 엘리먼트가 viewport 에 들어오면 onIntersect 를 호출. 보통 페이징의
 * fetchNextPage 와 연결.
 *
 * 사용 예:
 *   const query = usePagedListQuery({...});
 *   const sentinelRef = useInfiniteScroll({
 *     enabled: query.hasNextPage && !query.isFetchingNextPage,
 *     onIntersect: () => query.fetchNextPage(),
 *   });
 *   return (
 *     <>
 *       {items.map(...)}
 *       <div ref={sentinelRef} />
 *     </>
 *   );
 *
 * 정책:
 *   - rootMargin '200px' — 화면 끝 200px 전에 미리 트리거 (체감 끊김 방지)
 *   - enabled=false 면 observer detach (불필요한 트리거 방지)
 *   - onIntersect ref 로 보관해 sentinel 재등록 없이 최신 callback 사용
 */
import { useEffect, useRef, useCallback } from "react";

export interface UseInfiniteScrollOptions {
  /** false 면 observer 비활성 (다음 페이지 없음 / 로딩 중 등). */
  enabled: boolean;
  /** sentinel 가시 진입 시 호출. */
  onIntersect: () => void;
  /** IntersectionObserver rootMargin. 기본 "200px". */
  rootMargin?: string;
  /** IntersectionObserver root. 기본 viewport. 스크롤 컨테이너가 따로면 그 ref.current 전달. */
  root?: Element | null;
}

export function useInfiniteScroll({
  enabled,
  onIntersect,
  rootMargin = "200px",
  root = null,
}: UseInfiniteScrollOptions): (node: HTMLElement | null) => void {
  const onIntersectRef = useRef(onIntersect);
  onIntersectRef.current = onIntersect;

  const observerRef = useRef<IntersectionObserver | null>(null);
  const currentNodeRef = useRef<HTMLElement | null>(null);

  // sentinel DOM 노드 부착/해제. React 의 callback ref 패턴 — 같은 노드가 들어오면 no-op.
  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current && currentNodeRef.current) {
        observerRef.current.unobserve(currentNodeRef.current);
      }
      currentNodeRef.current = node;
      if (node && observerRef.current) {
        observerRef.current.observe(node);
      }
    },
    [],
  );

  useEffect(() => {
    if (!enabled) {
      observerRef.current?.disconnect();
      observerRef.current = null;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onIntersectRef.current();
            return;
          }
        }
      },
      { root, rootMargin, threshold: 0 },
    );
    observerRef.current = observer;
    if (currentNodeRef.current) {
      observer.observe(currentNodeRef.current);
    }

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [enabled, rootMargin, root]);

  return sentinelRef;
}
